import { watch, type Ref } from 'vue';
import { toast } from 'vue-sonner';
import { useQueryClient } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import http from '@/services/http';
import { useAuthStore } from '@/stores/authStore';
import { usePersistentTableStore } from '@/stores/persistentTableStore';
import {
  migrateLocalSavedViews,
  hasMigratedSavedViews,
  claimPendingSavedViews,
  parkPendingSavedViews,
  unparkSavedViewsFor,
  clearGuessedOwner,
  clearParkedOwner,
  ownerWasGuessedFor,
  hasPendingSavedViews,
  recordFailedMigrationAttempt,
  savedViewMigrationHasGivenUp,
  type PendingSavedViewUpload,
} from '@/utils/savedViewMigration';
import { SAVED_VIEWS_QUERY_PREFIX } from '@/composables/api/useSavedViewsApi';

/**
 * Fires the one-time localStorage -> server migration (SAU-310).
 *
 * WHY THIS IS ITS OWN MODULE, AND NOT A FEW LINES INSIDE useSavedViews.
 *
 * useSavedViews is called by all four table headers plus TrackMatchingPage, and
 * EditableSearchTableHeader renders its control TWICE — so a single search page
 * can hold five or six instances of it. Putting the trigger inline would mean
 * five concurrent migrations racing over the same localStorage blob and the same
 * uploads. The module-level guard below makes it exactly once per page load, no
 * matter how many instances ask.
 *
 * WHEN IT RUNS. On the FIRST SUCCESSFUL saved-views query, never at bootstrap.
 * The query is `retry: false`, so a backend that is not deployed yet fails fast
 * and this never fires — which is what lets the frontend ship first without
 * touching anyone's local data. It is also why `normalizeTableState`'s
 * savedViews drop is gated on the migration flag: nothing may destroy the local
 * copy before this has read it.
 */

/**
 * Guards against concurrent instances within one page load. Deliberately module
 * scope, not a ref: it must be shared by every useSavedViews caller.
 * `hasMigratedSavedViews()` is the durable, across-reloads half.
 */
let migrationStarted = false;

/** Reset between tests. Not used in production. */
export function resetSavedViewMigrationRunner(): void {
  migrationStarted = false;
}


export function useSavedViewMigrationRunner(isQuerySuccessful: Ref<boolean>): void {
  const queryClient = useQueryClient();
  const authStore = useAuthStore();

  watch(
    isQuerySuccessful,
    async (succeeded) => {
      if (!succeeded || migrationStarted) {
        return;
      }

      // BEFORE the flag check, because the flag is what would stop it: views that
      // were set aside while another account used this browser come back to
      // their owner here, and the ordinary migration then runs for them.
      const returningUserId = authStore.userData?.id;
      const unparkedBlob = returningUserId !== undefined ? unparkSavedViewsFor(returningUserId) : null;

      // savedViewMigrationHasGivenUp is checked HERE and not only inside migrate,
      // because the existing-names fetch below happens first: without it that GET
      // went out on every page load even after the migration had stopped trying.
      if (hasMigratedSavedViews() || savedViewMigrationHasGivenUp()) {
        return;
      }
      migrationStarted = true;

      // WHOSE VIEWS ARE THESE? `persistentTableStore` is a browser-wide key with no
      // user in it, so on a shared machine the answer is not "the person logged in
      // now". Asked here, at the upload, rather than by destroying the views when a
      // session ends — which also fired on an ordinary expired refresh token and
      // took a single user's own views with it.
      const currentUserId = authStore.userData?.id;
      if (hasPendingSavedViews()) {
        if (currentUserId === undefined) {
          // The query settled, so we ARE authenticated — but without the user's id
          // there is no way to tell their own views from the last person's, and
          // uploading blind is the outcome this whole check exists to prevent.
          // Nothing is written; the next page load asks again.
          migrationStarted = false;
          return;
        }
        if (claimPendingSavedViews(currentUserId) === 'someone-else') {
          // Set aside, not uploaded and not deleted. Nothing here belongs to the
          // account that is logged in.
          parkPendingSavedViews();
          // Released, like every other early return in this watcher — and here it
          // is load-bearing rather than tidy. Signing out does not reload the SPA,
          // so this module-scope flag survives the whole tab. Leaving it set meant
          // the owner signing back in hit the guard on the first line and returned
          // before the unpark above could hand their views back: an empty menu
          // until somebody hard-refreshed.
          migrationStarted = false;

          return;
        }
      }


      /**
       * Send one view. EVERY failure propagates, a 409 included.
       *
       * A 409 used to be swallowed as a skip, on the reasoning that the server
       * already held this view — the same one arriving from the user's second
       * browser. Checked against the running server, that is the 200 case, not
       * the 409 one:
       *
       *   first save                      -> 201
       *   same name, identical definition -> 200  (absorbed)
       *   same name, different definition -> 409
       *
       * So a 409 means the server holds something ELSE under that name and this
       * view was not saved — and skipping it cleared the browser's copy anyway.
       * Two machines with a view called "Online players", columns A and B on one
       * and C and D on the other: whichever synced second lost its version, under
       * a success message. The same path silently dropped duplicates within a
       * single browser too, "Report" and "report" for one page.
       *
       * Letting it through keeps localStorage intact — the 'partial' outcome
       * leaves the blob alone for retry — and reports the count to Sentry, so the
       * data survives and somebody hears about it.
       */
      const upload = async (view: PendingSavedViewUpload): Promise<void> => {
        await http.post('/api/saved_view', {
          entity: view.entity,
          name: view.name,
          definition: view.definition,
        });
      };

      let outcome;
      try {
        // The unparked blob is handed over directly: by the time the saved-views
        // query has settled, the persist plugin may already have rewritten the key
        // from in-memory state, which has no savedViews in it.
        outcome = await migrateLocalSavedViews(upload, unparkedBlob);
      } catch (error) {
        // migrateLocalSavedViews handles its own expected failures and returns an
        // outcome; reaching here means something unforeseen. Report rather than
        // swallow, and let the next load try again — a bounded number of times.
        Sentry.captureException(error);
        recordFailedMigrationAttempt('the migration threw', false);

        return;
      }

      if (outcome === 'partial' || outcome === 'blocked') {
        // Both leave localStorage intact and the flag unset on purpose, so the next
        // PAGE LOAD tries again — an immediate retry would only hit whatever made it
        // fail. The guard therefore stays set for the rest of this load. Clearing it
        // here did the opposite of what that sentence describes: the watcher is
        // `immediate`, and a query observer built on an already-cached key reports
        // success synchronously, so every table header mounted afterwards started a
        // fresh attempt. Clicking between table pages could spend the whole
        // five-attempt budget without a single reload. The module scope is what makes
        // "this page load" mean anything; a real reload re-evaluates it.
        return;
      }

      if (outcome === 'migrated') {
        // Say so when the views were claimed on a guess. Nothing before SAU-310
        // recorded who saved a local view, so the first person to sign in on this
        // browser after the deploy is assumed to own whatever was left in it — on a
        // shared machine, that may be somebody else's filters arriving in their
        // account under their name. It cannot be prevented after the fact, but it
        // does not have to happen in silence.
        if (currentUserId !== undefined && ownerWasGuessedFor(currentUserId)) {
          toast.info(
            'Saved views found in this browser have been added to your account. '
            + 'If any of them are not yours, you can delete them from the saved-view menu.',
          );
          clearGuessedOwner();
        }
        // Everything reached the server, so there is nothing left to hand back.
        clearParkedOwner();
        // The migration strips the retired keys from localStorage, but this store
        // was hydrated before it ran and the persist plugin rewrites storage from
        // IN-MEMORY state on the next mutation. Without this, the user's next column
        // resize put every scope's views back on disk, beside the backup copy.
        usePersistentTableStore().dropMigratedSavedViewKeys();
        // The prefix, not one entity's key: the blob can hold views for several
        // scopes, so every saved-views list the app is holding is now behind.
        queryClient.invalidateQueries({ queryKey: SAVED_VIEWS_QUERY_PREFIX });
      }
    },
    { immediate: true },
  );
}
